/**
 * PitchStudio — Multilingual teleprompter + camera/audio recorder (v50.10.1).
 * Position: after AudioExtractor in Déesse (FilmIA.jsx). Premium-gated for free users.
 * Admins bypass premium.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mic, Video, Wand2, Languages, Play, Pause, StopCircle, Download, Loader2,
  Sparkles, FileText, ChevronsUpDown, Copy, Trash2, Camera, Radio, Lock,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { ReportAIContentButton } from '../ReportAIContent';

const TR = {
  fr: { title: 'Studio Pitch', sub: 'Rédige, traduis, lis ton discours au téléprompteur puis enregistre-le en vidéo ou audio.',
    topic: 'Sujet du pitch', context: 'Contexte / points clés (optionnel)', tone: 'Ton', duration: 'Durée souhaitée',
    generate: 'Rédiger avec IA', translate: 'Traduire en',
    scrollSpeed: 'Vitesse défilement', startTele: 'Démarrer téléprompteur', pauseTele: 'Pause', resetTele: 'Retour haut',
    recordVideo: 'Enregistrer vidéo (5 min max)', recordAudio: 'Enregistrer audio (10 min max)', stop: 'Arrêter',
    download: 'Télécharger', clear: 'Effacer', copy: 'Copier le texte',
    placeholder: 'Colle ou écris ton pitch ici… ou clique "Rédiger avec IA" plus haut.',
    engaging: 'Dynamique', professional: 'Professionnel', friendly: 'Amical', passionate: 'Passionné',
    perms: 'Autorise la caméra et le micro pour enregistrer.', ready: 'Prêt', recording: 'Enregistrement…', done: 'Terminé',
    premiumTitle: 'Studio Pitch — Premium', premiumMsg: 'Cette fonctionnalité est incluse dans l\u2019abonnement Premium Déesse : téléprompteur, rédaction IA, traduction en 13 langues et enregistrement vidéo/audio.', premiumBtn: 'Découvrir Premium' },
  en: { title: 'Pitch Studio', sub: 'Write, translate, read your speech on the teleprompter, then record it as video or audio.',
    topic: 'Pitch topic', context: 'Context / key points (optional)', tone: 'Tone', duration: 'Target duration',
    generate: 'Write with AI', translate: 'Translate to',
    scrollSpeed: 'Scroll speed', startTele: 'Start teleprompter', pauseTele: 'Pause', resetTele: 'Reset',
    recordVideo: 'Record video (5 min max)', recordAudio: 'Record audio (10 min max)', stop: 'Stop',
    download: 'Download', clear: 'Clear', copy: 'Copy text',
    placeholder: 'Paste or write your pitch here… or click "Write with AI" above.',
    engaging: 'Engaging', professional: 'Professional', friendly: 'Friendly', passionate: 'Passionate',
    perms: 'Grant camera and mic access to record.', ready: 'Ready', recording: 'Recording…', done: 'Done',
    premiumTitle: 'Pitch Studio — Premium', premiumMsg: 'This feature is included in the Déesse Premium subscription: teleprompter, AI writing, 13-language translation and video/audio recording.', premiumBtn: 'Discover Premium' },
  es: { title: 'Estudio Pitch', sub: 'Escribe, traduce, lee tu discurso en el teleprompter y grábalo en vídeo o audio.',
    topic: 'Tema del pitch', context: 'Contexto / puntos clave (opcional)', tone: 'Tono', duration: 'Duración deseada',
    generate: 'Redactar con IA', translate: 'Traducir a',
    scrollSpeed: 'Velocidad', startTele: 'Iniciar teleprompter', pauseTele: 'Pausa', resetTele: 'Volver arriba',
    recordVideo: 'Grabar vídeo (5 min máx)', recordAudio: 'Grabar audio (10 min máx)', stop: 'Detener',
    download: 'Descargar', clear: 'Borrar', copy: 'Copiar texto',
    placeholder: 'Pega o escribe tu pitch aquí… o pulsa "Redactar con IA".',
    engaging: 'Dinámico', professional: 'Profesional', friendly: 'Amistoso', passionate: 'Apasionado',
    perms: 'Autoriza la cámara y el micrófono para grabar.', ready: 'Listo', recording: 'Grabando…', done: 'Terminado',
    premiumTitle: 'Estudio Pitch — Premium', premiumMsg: 'Esta función está incluida en la suscripción Premium Déesse: teleprompter, redacción IA, traducción a 13 idiomas y grabación vídeo/audio.', premiumBtn: 'Descubrir Premium' },
  pt: { title: 'Estúdio Pitch', sub: 'Escreva, traduza, leia o seu discurso no teleponto e grave-o em vídeo ou áudio.',
    topic: 'Tema do pitch', context: 'Contexto / pontos-chave (opcional)', tone: 'Tom', duration: 'Duração desejada',
    generate: 'Redigir com IA', translate: 'Traduzir para',
    scrollSpeed: 'Velocidade', startTele: 'Iniciar teleponto', pauseTele: 'Pausa', resetTele: 'Voltar ao topo',
    recordVideo: 'Gravar vídeo (5 min máx)', recordAudio: 'Gravar áudio (10 min máx)', stop: 'Parar',
    download: 'Baixar', clear: 'Apagar', copy: 'Copiar texto',
    placeholder: 'Cole ou escreva o seu pitch aqui… ou clique em "Redigir com IA".',
    engaging: 'Dinâmico', professional: 'Profissional', friendly: 'Amigável', passionate: 'Apaixonado',
    perms: 'Autorize a câmera e o microfone para gravar.', ready: 'Pronto', recording: 'Gravando…', done: 'Concluído',
    premiumTitle: 'Estúdio Pitch — Premium', premiumMsg: 'Este recurso está incluído na assinatura Premium Déesse: teleponto, redação IA, tradução em 13 idiomas e gravação vídeo/áudio.', premiumBtn: 'Descobrir o Premium' },
  de: { title: 'Pitch-Studio', sub: 'Schreibe, übersetze, lies deine Rede am Teleprompter und nimm sie als Video oder Audio auf.',
    topic: 'Pitch-Thema', context: 'Kontext / Kernpunkte (optional)', tone: 'Ton', duration: 'Gewünschte Dauer',
    generate: 'Mit KI schreiben', translate: 'Übersetzen in',
    scrollSpeed: 'Scrollgeschwindigkeit', startTele: 'Teleprompter starten', pauseTele: 'Pause', resetTele: 'Nach oben',
    recordVideo: 'Video aufnehmen (max. 5 Min)', recordAudio: 'Audio aufnehmen (max. 10 Min)', stop: 'Stoppen',
    download: 'Herunterladen', clear: 'Löschen', copy: 'Text kopieren',
    placeholder: 'Füge deinen Pitch hier ein… oder klicke auf "Mit KI schreiben".',
    engaging: 'Dynamisch', professional: 'Professionell', friendly: 'Freundlich', passionate: 'Leidenschaftlich',
    perms: 'Erlaube Kamera und Mikrofon zum Aufnehmen.', ready: 'Bereit', recording: 'Aufnahme…', done: 'Fertig',
    premiumTitle: 'Pitch-Studio — Premium', premiumMsg: 'Diese Funktion ist im Déesse Premium-Abo enthalten: Teleprompter, KI-Texte, Übersetzung in 13 Sprachen und Video-/Audioaufnahme.', premiumBtn: 'Premium entdecken' },
  ar: { title: 'استوديو العرض', sub: 'اكتب وترجم واقرأ خطابك على الملقّن ثم سجّله فيديو أو صوتًا.',
    topic: 'موضوع العرض', context: 'السياق / النقاط الرئيسية (اختياري)', tone: 'النبرة', duration: 'المدة المطلوبة',
    generate: 'الكتابة بالذكاء الاصطناعي', translate: 'ترجمة إلى',
    scrollSpeed: 'سرعة التمرير', startTele: 'تشغيل الملقّن', pauseTele: 'إيقاف مؤقت', resetTele: 'العودة للأعلى',
    recordVideo: 'تسجيل فيديو (5 دقائق كحد أقصى)', recordAudio: 'تسجيل صوتي (10 دقائق كحد أقصى)', stop: 'إيقاف',
    download: 'تحميل', clear: 'مسح', copy: 'نسخ النص',
    placeholder: 'الصق أو اكتب عرضك هنا… أو اضغط "الكتابة بالذكاء الاصطناعي".',
    engaging: 'حيوي', professional: 'احترافي', friendly: 'ودّي', passionate: 'حماسي',
    perms: 'اسمح بالكاميرا والميكروفون للتسجيل.', ready: 'جاهز', recording: 'جارٍ التسجيل…', done: 'تم',
    premiumTitle: 'استوديو العرض — بريميوم', premiumMsg: 'هذه الميزة مشمولة في اشتراك Déesse بريميوم: ملقّن، كتابة بالذكاء الاصطناعي، ترجمة إلى 13 لغة وتسجيل فيديو/صوت.', premiumBtn: 'اكتشف بريميوم' },
  ru: { title: 'Питч-студия', sub: 'Напишите, переведите, читайте речь с телесуфлёра и запишите её как видео или аудио.',
    topic: 'Тема питча', context: 'Контекст / ключевые пункты (необязательно)', tone: 'Тон', duration: 'Желаемая длительность',
    generate: 'Написать с ИИ', translate: 'Перевести на',
    scrollSpeed: 'Скорость', startTele: 'Запустить суфлёр', pauseTele: 'Пауза', resetTele: 'Наверх',
    recordVideo: 'Записать видео (макс. 5 мин)', recordAudio: 'Записать аудио (макс. 10 мин)', stop: 'Стоп',
    download: 'Скачать', clear: 'Очистить', copy: 'Копировать текст',
    placeholder: 'Вставьте или напишите свой питч… или нажмите «Написать с ИИ».',
    engaging: 'Динамичный', professional: 'Профессиональный', friendly: 'Дружелюбный', passionate: 'Страстный',
    perms: 'Разрешите камеру и микрофон для записи.', ready: 'Готово', recording: 'Запись…', done: 'Готово',
    premiumTitle: 'Питч-студия — Premium', premiumMsg: 'Эта функция включена в подписку Déesse Premium: телесуфлёр, ИИ-тексты, перевод на 13 языков и запись видео/аудио.', premiumBtn: 'Узнать о Premium' },
  zh: { title: '演讲工作室', sub: '撰写、翻译，在提词器上朗读你的演讲，然后录制视频或音频。',
    topic: '演讲主题', context: '背景 / 要点（可选）', tone: '语气', duration: '目标时长',
    generate: '用 AI 撰写', translate: '翻译为',
    scrollSpeed: '滚动速度', startTele: '启动提词器', pauseTele: '暂停', resetTele: '回到顶部',
    recordVideo: '录制视频（最长 5 分钟）', recordAudio: '录制音频（最长 10 分钟）', stop: '停止',
    download: '下载', clear: '清空', copy: '复制文本',
    placeholder: '在此粘贴或撰写你的演讲…或点击上方"用 AI 撰写"。',
    engaging: '活力', professional: '专业', friendly: '亲切', passionate: '热情',
    perms: '请授权摄像头和麦克风以录制。', ready: '就绪', recording: '录制中…', done: '完成',
    premiumTitle: '演讲工作室 — 高级版', premiumMsg: '此功能包含在 Déesse 高级订阅中：提词器、AI 撰写、13 种语言翻译以及视频/音频录制。', premiumBtn: '了解高级版' },
  ja: { title: 'ピッチスタジオ', sub: '原稿を書き、翻訳し、テレプロンプターで読みながら動画または音声で録画・録音できます。',
    topic: 'ピッチのテーマ', context: '背景 / 要点（任意）', tone: 'トーン', duration: '希望の長さ',
    generate: 'AIで作成', translate: '翻訳する',
    scrollSpeed: 'スクロール速度', startTele: 'プロンプター開始', pauseTele: '一時停止', resetTele: '先頭に戻る',
    recordVideo: '動画を録画（最大5分）', recordAudio: '音声を録音（最大10分）', stop: '停止',
    download: 'ダウンロード', clear: '消去', copy: 'テキストをコピー',
    placeholder: 'ここにピッチを貼り付けるか入力…または上の「AIで作成」をタップ。',
    engaging: 'ダイナミック', professional: 'プロフェッショナル', friendly: 'フレンドリー', passionate: '情熱的',
    perms: '録画にはカメラとマイクの許可が必要です。', ready: '準備完了', recording: '録画中…', done: '完了',
    premiumTitle: 'ピッチスタジオ — プレミアム', premiumMsg: 'この機能は Déesse プレミアムに含まれます：プロンプター、AI作成、13言語翻訳、動画/音声録画。', premiumBtn: 'プレミアムを見る' },
  hi: { title: 'पिच स्टूडियो', sub: 'अपना भाषण लिखें, अनुवाद करें, टेलीप्रॉम्प्टर पर पढ़ें और वीडियो या ऑडियो में रिकॉर्ड करें।',
    topic: 'पिच का विषय', context: 'संदर्भ / मुख्य बिंदु (वैकल्पिक)', tone: 'लहजा', duration: 'वांछित अवधि',
    generate: 'AI से लिखें', translate: 'अनुवाद करें',
    scrollSpeed: 'स्क्रॉल गति', startTele: 'टेलीप्रॉम्प्टर शुरू करें', pauseTele: 'रोकें', resetTele: 'ऊपर जाएँ',
    recordVideo: 'वीडियो रिकॉर्ड करें (अधिकतम 5 मिनट)', recordAudio: 'ऑडियो रिकॉर्ड करें (अधिकतम 10 मिनट)', stop: 'बंद करें',
    download: 'डाउनलोड', clear: 'साफ़ करें', copy: 'टेक्स्ट कॉपी करें',
    placeholder: 'यहाँ अपना पिच लिखें या पेस्ट करें… या ऊपर "AI से लिखें" दबाएँ।',
    engaging: 'गतिशील', professional: 'पेशेवर', friendly: 'मैत्रीपूर्ण', passionate: 'जोशीला',
    perms: 'रिकॉर्ड करने के लिए कैमरा और माइक की अनुमति दें।', ready: 'तैयार', recording: 'रिकॉर्डिंग…', done: 'पूर्ण',
    premiumTitle: 'पिच स्टूडियो — प्रीमियम', premiumMsg: 'यह सुविधा Déesse प्रीमियम सदस्यता में शामिल है: टेलीप्रॉम्प्टर, AI लेखन, 13 भाषाओं में अनुवाद और वीडियो/ऑडियो रिकॉर्डिंग।', premiumBtn: 'प्रीमियम देखें' },
  nl: { title: 'Pitch Studio', sub: 'Schrijf, vertaal, lees je toespraak op de teleprompter en neem hem op als video of audio.',
    topic: 'Pitch-onderwerp', context: 'Context / kernpunten (optioneel)', tone: 'Toon', duration: 'Gewenste duur',
    generate: 'Schrijven met AI', translate: 'Vertalen naar',
    scrollSpeed: 'Scrollsnelheid', startTele: 'Teleprompter starten', pauseTele: 'Pauze', resetTele: 'Naar boven',
    recordVideo: 'Video opnemen (max 5 min)', recordAudio: 'Audio opnemen (max 10 min)', stop: 'Stoppen',
    download: 'Downloaden', clear: 'Wissen', copy: 'Tekst kopiëren',
    placeholder: 'Plak of schrijf je pitch hier… of klik op "Schrijven met AI".',
    engaging: 'Dynamisch', professional: 'Professioneel', friendly: 'Vriendelijk', passionate: 'Gepassioneerd',
    perms: 'Geef camera- en microfoontoegang om op te nemen.', ready: 'Klaar', recording: 'Opname…', done: 'Klaar',
    premiumTitle: 'Pitch Studio — Premium', premiumMsg: 'Deze functie zit in het Déesse Premium-abonnement: teleprompter, AI-teksten, vertaling in 13 talen en video-/audio-opname.', premiumBtn: 'Ontdek Premium' },
  sw: { title: 'Studio ya Pitch', sub: 'Andika, tafsiri, soma hotuba yako kwenye teleprompter kisha uirekodi kama video au sauti.',
    topic: 'Mada ya pitch', context: 'Muktadha / hoja kuu (hiari)', tone: 'Mtindo', duration: 'Muda unaotaka',
    generate: 'Andika kwa AI', translate: 'Tafsiri kwa',
    scrollSpeed: 'Kasi ya kusogeza', startTele: 'Anzisha teleprompter', pauseTele: 'Simamisha', resetTele: 'Rudi juu',
    recordVideo: 'Rekodi video (dakika 5 max)', recordAudio: 'Rekodi sauti (dakika 10 max)', stop: 'Acha',
    download: 'Pakua', clear: 'Futa', copy: 'Nakili maandishi',
    placeholder: 'Bandika au andika pitch yako hapa… au bonyeza "Andika kwa AI".',
    engaging: 'Changamfu', professional: 'Kitaalamu', friendly: 'Rafiki', passionate: 'Shauku',
    perms: 'Ruhusu kamera na kipaza sauti ili kurekodi.', ready: 'Tayari', recording: 'Inarekodi…', done: 'Imekamilika',
    premiumTitle: 'Studio ya Pitch — Premium', premiumMsg: 'Kipengele hiki kimo katika usajili wa Déesse Premium: teleprompter, uandishi wa AI, tafsiri lugha 13 na kurekodi video/sauti.', premiumBtn: 'Gundua Premium' },
  ht: { title: 'Estidyo Pitch', sub: 'Ekri, tradui, li diskou ou sou telepwonptè a epi anrejistre li an videyo oswa odyo.',
    topic: 'Sijè pitch la', context: 'Kontèks / pwen kle yo (opsyonèl)', tone: 'Ton', duration: 'Dire ou vle',
    generate: 'Ekri ak IA', translate: 'Tradui an',
    scrollSpeed: 'Vitès defile', startTele: 'Kòmanse telepwonptè', pauseTele: 'Poz', resetTele: 'Tounen anwo',
    recordVideo: 'Anrejistre videyo (5 min max)', recordAudio: 'Anrejistre odyo (10 min max)', stop: 'Kanpe',
    download: 'Telechaje', clear: 'Efase', copy: 'Kopye tèks la',
    placeholder: 'Kole oswa ekri pitch ou isit la… oswa klike "Ekri ak IA".',
    engaging: 'Dinamik', professional: 'Pwofesyonèl', friendly: 'Amikal', passionate: 'Pasyone',
    perms: 'Otorize kamera ak mikwo pou anrejistre.', ready: 'Pare', recording: 'Anrejistreman…', done: 'Fini',
    premiumTitle: 'Estidyo Pitch — Premium', premiumMsg: 'Fonksyonalite sa a enkli nan abònman Premium Déesse: telepwonptè, redaksyon IA, tradiksyon nan 13 lang ak anrejistreman videyo/odyo.', premiumBtn: 'Dekouvri Premium' },
};

const t = (lang, key) => (TR[lang] || TR.en)[key] || TR.fr[key];
export const pitchTexts = (lang) => TR[lang] || TR.en;

const PitchStudio = ({ adminFree = false, onUpgrade }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [premiumOk, setPremiumOk] = useState(adminFree);
  const [premiumChecked, setPremiumChecked] = useState(adminFree);
  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [tone, setTone] = useState('engaging');
  const [duration, setDuration] = useState(90);
  const [text, setText] = useState('');
  const [languages, setLanguages] = useState({});
  const [targetLang, setTargetLang] = useState('en');
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Teleprompter
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const [teleRunning, setTeleRunning] = useState(false);
  const [teleSpeed, setTeleSpeed] = useState(0.8); // px per frame

  // Recording
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const previewVideoRef = useRef(null);
  const [recording, setRecording] = useState(null); // 'video' | 'audio' | null
  const [blobUrl, setBlobUrl] = useState(null);
  const [recSecs, setRecSecs] = useState(0);
  const secIvRef = useRef(null);

  useEffect(() => {
    axios.get('/api/pitch/languages').then(r => setLanguages(r.data.languages || {})).catch(() => {});
    return () => cleanup();
  }, []);

  // Premium gate — admins & adminFree embeds bypass, otherwise check subscription
  useEffect(() => {
    if (adminFree || user?.is_admin || user?.is_super_admin || user?.admin_role) { setPremiumOk(true); setPremiumChecked(true); return; }
    Promise.allSettled([axios.get('/api/subscription/settings'), axios.get('/api/subscription/mine')])
      .then(([cfg, mine]) => {
        const masterOn = cfg.status === 'fulfilled' ? !!cfg.value.data?.enabled : false;
        const active = mine.status === 'fulfilled' ? !!mine.value.data?.active : false;
        setPremiumOk(!masterOn || active); // v60.7 — Premium désactivé par l'admin = accès libre
        setPremiumChecked(true);
      });
  }, [adminFree, user?.is_admin, user?.is_super_admin, user?.admin_role]);

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (secIvRef.current) clearInterval(secIvRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  };

  const generate = async () => {
    if (!topic.trim()) { toast.error('Ajoute un sujet.'); return; }
    setBusy(true);
    try {
      const r = await axios.post('/api/pitch/generate', { topic, context, tone, duration_seconds: duration });
      setText(r.data.text || '');
      toast.success('Pitch rédigé ✨');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur IA');
    } finally { setBusy(false); }
  };

  const translate = async () => {
    if (!text.trim()) { toast.error('Écris ou génère d\'abord un pitch.'); return; }
    setTranslating(true);
    try {
      const r = await axios.post('/api/pitch/translate', { text, target_lang: targetLang });
      setText(r.data.text || '');
      toast.success(`Traduit → ${r.data.language_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur traduction');
    } finally { setTranslating(false); }
  };

  // v60.7 — Smart teleprompter: listens to the mic; scrolling pauses when the speaker stops talking
  const [smartMode, setSmartMode] = useState(true);
  const speakingRef = useRef(true);
  const vadRef = useRef(null);
  useEffect(() => {
    if (!teleRunning || !smartMode) { speakingRef.current = true; return; }
    let ctx, raf, stream, lastVoice = Date.now();
    navigator.mediaDevices?.getUserMedia({ audio: true }).then((st) => {
      stream = st;
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(st);
      const an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const loop = () => {
        an.getByteFrequencyData(buf);
        const level = buf.reduce((a, b) => a + b, 0) / buf.length;
        if (level > 18) lastVoice = Date.now();
        speakingRef.current = Date.now() - lastVoice < 1200;
        raf = requestAnimationFrame(loop);
      };
      loop();
    }).catch(() => { speakingRef.current = true; });
    vadRef.current = () => { if (raf) cancelAnimationFrame(raf); if (stream) stream.getTracks().forEach(t => t.stop()); if (ctx) ctx.close().catch(() => {}); };
    return () => vadRef.current && vadRef.current();
  }, [teleRunning, smartMode]);

  // Teleprompter scroll
  useEffect(() => {
    if (!teleRunning) return;
    const step = () => {
      const el = scrollRef.current;
      if (el && speakingRef.current) {
        el.scrollTop += teleSpeed;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) setTeleRunning(false);
      }
      if (teleRunning) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [teleRunning, teleSpeed]);

  // Recording
  const startRec = async (kind) => {
    const maxSec = kind === 'video' ? 300 : 600;
    try {
      const constraints = kind === 'video'
        ? { video: { width: { ideal: 1080 }, height: { ideal: 1920 }, facingMode: 'user' }, audio: true }
        : { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000 } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (kind === 'video' && previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        previewVideoRef.current.muted = true;
        previewVideoRef.current.play().catch(() => {});
      }
      const mime = kind === 'video'
        ? (MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm;codecs=vp9,opus')
        : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');
      const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 192000, videoBitsPerSecond: 4_000_000 });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setBlobUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
      };
      rec.start(1000);
      mediaRef.current = rec;
      setRecording(kind);
      setRecSecs(0);
      secIvRef.current = setInterval(() => setRecSecs(s => {
        if (s + 1 >= maxSec) { stopRec(); return s + 1; }
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error(t(language, 'perms'));
    }
  };

  const stopRec = () => {
    try { mediaRef.current && mediaRef.current.state !== 'inactive' && mediaRef.current.stop(); } catch { /* ignore */ }
    if (secIvRef.current) clearInterval(secIvRef.current);
    setRecording(null);
  };

  const downloadBlob = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `pitch_${Date.now()}.${recording === 'video' ? 'mp4' : 'webm'}`;
    a.click();
  };

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-pink-500/10 to-fuchsia-500/10 border border-pink-500/30 space-y-3" data-testid="pitch-studio">
      <div>
        <h3 className="font-bold text-base flex items-center gap-2"><FileText className="w-4 h-4 text-fuchsia-400" /> {t(language, 'title')}</h3>
        <p className="text-xs text-white/60 mt-0.5">{t(language, 'sub')}</p>
      </div>

      {premiumChecked && !premiumOk ? (
        <div className="p-4 rounded-xl bg-black/40 border border-fuchsia-500/30 text-center space-y-2" data-testid="pitch-premium-lock">
          <Lock className="w-6 h-6 text-fuchsia-400 mx-auto" />
          <p className="font-semibold text-sm text-white">{t(language, 'premiumTitle')}</p>
          <p className="text-xs text-white/60">{t(language, 'premiumMsg')}</p>
          {onUpgrade && (
            <button onClick={onUpgrade}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-xs font-semibold"
              data-testid="pitch-premium-upgrade">
              {t(language, 'premiumBtn')}
            </button>
          )}
        </div>
      ) : !premiumChecked ? (
        <div className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-400" /></div>
      ) : (
      <>
      {/* AI generator inputs */}
      <div className="space-y-2">
        <input value={topic} onChange={e => setTopic(e.target.value)} placeholder={t(language, 'topic')}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="pitch-topic" />
        <textarea value={context} onChange={e => setContext(e.target.value)} placeholder={t(language, 'context')} rows={2}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white resize-none" data-testid="pitch-context" />
        <div className="grid grid-cols-2 gap-2">
          <select value={tone} onChange={e => setTone(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-xl px-2 py-2 text-xs text-white" data-testid="pitch-tone">
            <option value="engaging">{t(language, 'engaging')}</option>
            <option value="professional">{t(language, 'professional')}</option>
            <option value="friendly">{t(language, 'friendly')}</option>
            <option value="passionate">{t(language, 'passionate')}</option>
          </select>
          <div className="flex items-center gap-1.5">
            <input type="number" min={15} max={600} step={5} value={duration} onChange={e => setDuration(parseInt(e.target.value || '60', 10))}
              className="flex-1 bg-black/30 border border-white/10 rounded-xl px-2 py-2 text-xs text-white text-center" data-testid="pitch-duration" />
            <span className="text-[10px] text-white/50">sec</span>
          </div>
        </div>
        <button onClick={generate} disabled={busy || !topic.trim()}
          className="w-full py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
          data-testid="pitch-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {t(language, 'generate')}
        </button>
      </div>

      {/* Text editor + translate */}
      <div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={8}
          placeholder={t(language, 'placeholder')}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-3 text-sm text-white leading-relaxed resize-none"
          data-testid="pitch-text" />
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <select value={targetLang} onChange={e => setTargetLang(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-xl px-2 py-1.5 text-xs text-white" data-testid="pitch-lang-target">
            {Object.entries(languages).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
          <button onClick={translate} disabled={translating || !text.trim()}
            className="px-3 py-1.5 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="pitch-translate">
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            {t(language, 'translate')}
          </button>
          <button onClick={() => { navigator.clipboard.writeText(text); toast.success('Copié'); }} disabled={!text}
            className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
            <Copy className="w-3 h-3" /> {t(language, 'copy')}
          </button>
          <button onClick={() => setText('')} disabled={!text}
            className="px-3 py-1.5 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-200 text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
            <Trash2 className="w-3 h-3" /> {t(language, 'clear')}
          </button>
        </div>
      </div>

      {/* Teleprompter */}
      {text && (
        <div className="rounded-xl bg-black/60 border border-white/10 overflow-hidden">
          <div ref={scrollRef} className="h-64 overflow-y-auto p-4 text-white text-xl leading-relaxed whitespace-pre-wrap"
            data-testid="pitch-teleprompter"
            style={{ textAlign: 'center', scrollBehavior: 'auto' }}>
            {text}
            <div style={{ height: '60%' }} />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 border-t border-white/10 bg-black/40">
            <button onClick={() => setTeleRunning(r => !r)}
              className="px-3 py-1.5 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs inline-flex items-center gap-1.5"
              data-testid="pitch-tele-toggle">
              {teleRunning ? <><Pause className="w-3 h-3" /> {t(language, 'pauseTele')}</> : <><Play className="w-3 h-3" /> {t(language, 'startTele')}</>}
            </button>
            <button onClick={() => setSmartMode(m => !m)} title="Pause auto quand tu ne parles plus"
              className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 ${smartMode ? 'bg-fuchsia-500/30 text-fuchsia-100' : 'bg-white/10 text-white/60'}`}
              data-testid="pitch-tele-smart">
              <Mic className="w-3 h-3" /> {smartMode ? 'Auto' : 'Manuel'}
            </button>
            <button onClick={() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; setTeleRunning(false); }}
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs inline-flex items-center gap-1.5">
              <ChevronsUpDown className="w-3 h-3" /> {t(language, 'resetTele')}
            </button>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[10px] text-white/60">{t(language, 'scrollSpeed')}</span>
              <input type="range" min="0.2" max="3" step="0.1" value={teleSpeed} onChange={e => setTeleSpeed(parseFloat(e.target.value))}
                className="w-20 accent-fuchsia-500" data-testid="pitch-tele-speed" />
            </div>
          </div>
        </div>
      )}

      {/* Recording */}
      <div className="rounded-xl bg-black/40 border border-white/10 p-3 space-y-2">
        {recording === 'video' && (
          <video ref={previewVideoRef} className="w-full rounded-lg bg-black" autoPlay playsInline muted data-testid="pitch-cam-preview" />
        )}
        {recording && (
          <div className="flex items-center gap-2 text-xs text-red-300 font-semibold">
            <Radio className="w-3.5 h-3.5 animate-pulse text-red-500" />
            {recording === 'video' ? '🎥' : '🎙️'} {t(language, 'recording')} — {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, '0')}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {!recording && (
            <>
              <button onClick={() => startRec('video')}
                className="flex-1 py-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="pitch-rec-video">
                <Video className="w-3.5 h-3.5" /> {t(language, 'recordVideo')}
              </button>
              <button onClick={() => startRec('audio')}
                className="flex-1 py-2 rounded-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                data-testid="pitch-rec-audio">
                <Mic className="w-3.5 h-3.5" /> {t(language, 'recordAudio')}
              </button>
            </>
          )}
          {recording && (
            <button onClick={stopRec}
              className="w-full py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5"
              data-testid="pitch-rec-stop">
              <StopCircle className="w-3.5 h-3.5" /> {t(language, 'stop')}
            </button>
          )}
        </div>

        {blobUrl && (
          <div className="pt-2 space-y-2">
            {recording === 'video' || blobUrl.includes('video')
              ? <video src={blobUrl} controls className="w-full rounded-lg" data-testid="pitch-rec-video-out" />
              : <audio src={blobUrl} controls className="w-full" data-testid="pitch-rec-audio-out" />}
            <button onClick={downloadBlob}
              className="w-full py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
              data-testid="pitch-download">
              <Download className="w-3.5 h-3.5" /> {t(language, 'download')}
            </button>
            <div className="flex justify-end"><ReportAIContentButton feature="pitch_studio" testId="ai-report-pitch" /></div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default PitchStudio;
